import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardPaste, Merge, Split, CheckCircle2, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, ROLES } from "@/lib/constants";
import { htmlToText, parseHandbook, sameWords } from "@/lib/handbook";
import { AudienceField } from "@/components/AudienceField";

const area = "w-full rounded-xl border border-lasso-line p-3 text-sm focus:border-lasso-ink focus:outline-none";

function StepRow({ s, i, total, onChange, onMerge, onSplit, onRemove }) {
  const [cursor, setCursor] = useState(null);
  return (
    <div className="rounded-xl border border-lasso-line p-3" data-testid={`paste-step-${i}`}>
      <div className="flex items-center justify-between text-xs font-semibold text-lasso-deep">Step {i + 1}
        <span className="flex gap-2">
          <button type="button" onClick={() => onSplit(cursor)} className="inline-flex items-center gap-1 hover:underline" data-testid={`paste-step-${i}-split`}><Split className="h-3.5 w-3.5" /> Split at cursor</button>
          {i < total - 1 && <button type="button" onClick={onMerge} className="inline-flex items-center gap-1 hover:underline" data-testid={`paste-step-${i}-merge`}><Merge className="h-3.5 w-3.5" /> Merge with next</button>}
          <button type="button" onClick={onRemove} className="text-zinc-400 hover:text-rose-600" data-testid={`paste-step-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
        </span>
      </div>
      <Input className="mt-2" value={s.title} onChange={(e) => onChange({ ...s, title: e.target.value })} data-testid={`paste-step-${i}-title`} />
      <textarea className={`${area} mt-2`} rows={Math.min(6, (s.detail.match(/\n/g) || []).length + 2)} value={s.detail} placeholder="Detail (optional)"
        onSelect={(e) => setCursor(e.target.selectionStart)} onChange={(e) => onChange({ ...s, detail: e.target.value })} data-testid={`paste-step-${i}-detail`} />
    </div>
  );
}

export function PasteHandbook() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [p, setP] = useState(null);
  const [meta, setMeta] = useState({ category: "", applicable_roles: [], audience_region_ids: [] });
  const [saving, setSaving] = useState(false);

  const onPaste = (e) => {
    const html = e.clipboardData.getData("text/html");
    if (!html) return;
    e.preventDefault();
    const text = htmlToText(html);
    const el = e.target;
    setRaw(raw.slice(0, el.selectionStart) + text + raw.slice(el.selectionEnd));
  };
  const steps = p?.steps || [];
  const setSteps = (s) => setP({ ...p, steps: s });
  const merge = (i) => {
    const a = steps[i], b = steps[i + 1];
    setSteps([...steps.slice(0, i), { title: a.title, detail: [a.detail, b.title, b.detail].filter(Boolean).join("\n") }, ...steps.slice(i + 2)]);
  };
  const split = (i, at) => {
    const s = steps[i];
    if (!s.detail) return toast.error("Put the cursor in the detail where the new step should start");
    const pos = at ?? s.detail.indexOf("\n");
    const head = s.detail.slice(0, pos > 0 ? pos : 0).trim(), tail = s.detail.slice(pos > 0 ? pos : 0).trim();
    if (!tail) return toast.error("Nothing after the cursor to split off");
    const [t, ...rest] = tail.split("\n");
    setSteps([...steps.slice(0, i), { ...s, detail: head }, { title: t.trim(), detail: rest.join("\n").trim() }, ...steps.slice(i + 1)]);
  };
  const save = async () => {
    if (!meta.category || !meta.applicable_roles.length) return toast.error("Choose a category and at least one role");
    if (!p.title.trim() || !steps.length || steps.some((s) => !s.title.trim())) return toast.error("A title and at least one titled step are required");
    setSaving(true);
    try {
      const { data } = await api.post("/sops", { ...p, ...meta, status: "draft" });
      toast.success("Saved as a draft. Review it, then publish.");
      navigate(`/sops/${data.id}/edit`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const exact = p && sameWords(raw, p);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-lasso-line bg-white px-4 py-2.5 text-sm font-semibold hover:border-lasso-ink" data-testid="sop-paste-handbook-button"><ClipboardPaste className="h-4 w-4" /> Paste from handbook</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="paste-handbook-dialog">
          <DialogHeader><DialogTitle className="font-display">Paste from handbook</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Paste one SOP. The first line becomes the title; “Summary:” and “Purpose:” sections are picked up if labelled; numbered or bulleted lines (or paragraphs) become steps. Wording is kept exactly; nothing is rewritten.</p>
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)} onPaste={onPaste} rows={p ? 6 : 14} className={`${area} font-mono`} placeholder="Paste the handbook section here (Word / Google Docs formatting is stripped)" data-testid="paste-handbook-textarea" />
          <button type="button" disabled={!raw.trim()} onClick={() => setP(parseHandbook(raw))} className="w-fit rounded-full bg-lasso-ink px-5 py-2 text-sm font-semibold text-white disabled:opacity-40" data-testid="paste-handbook-parse">{p ? "Re-parse" : "Split into sections"}</button>
          {p && (
            <div className="space-y-4" data-testid="paste-handbook-preview">
              <p className={`flex items-center gap-2 text-xs font-semibold ${exact ? "text-emerald-700" : "text-amber-800"}`} data-testid="paste-handbook-wording-check">
                {exact ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                {exact ? "Wording check: every word from the pasted text is kept, and nothing has been added." : "Wording differs from the pasted text (because of your edits)."}
              </p>
              <div className="space-y-1"><Label>Title</Label><Input value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} data-testid="paste-handbook-title" /></div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1"><Label>Summary</Label><textarea className={area} rows={3} value={p.summary} onChange={(e) => setP({ ...p, summary: e.target.value })} data-testid="paste-handbook-summary" /></div>
                <div className="space-y-1"><Label>Purpose</Label><textarea className={area} rows={3} value={p.purpose} onChange={(e) => setP({ ...p, purpose: e.target.value })} data-testid="paste-handbook-purpose" /></div>
              </div>
              <div className="space-y-2">
                <Label>Steps ({steps.length})</Label>
                {steps.map((s, i) => <StepRow key={i} s={s} i={i} total={steps.length} onChange={(v) => setSteps(steps.map((x, j) => (j === i ? v : x)))} onMerge={() => merge(i)} onSplit={(at) => split(i, at)} onRemove={() => setSteps(steps.filter((_, j) => j !== i))} />)}
              </div>
              <div className="grid gap-4 rounded-2xl bg-lasso-paper p-4 md:grid-cols-3">
                <div className="space-y-1"><Label>Category</Label>
                  <select value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} className="h-10 w-full rounded-xl border border-lasso-line bg-white px-3 text-sm" data-testid="paste-handbook-category">
                    <option value="">Choose…</option>{CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.value}</option>)}
                  </select>
                </div>
                <div className="space-y-1"><Label>Roles</Label>
                  {ROLES.map((r) => <label key={r.value} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={meta.applicable_roles.includes(r.value)} data-testid={`paste-handbook-role-${r.value}`}
                    onChange={() => setMeta({ ...meta, applicable_roles: meta.applicable_roles.includes(r.value) ? meta.applicable_roles.filter((x) => x !== r.value) : [...meta.applicable_roles, r.value] })} />{r.label}</label>)}
                </div>
                <AudienceField value={meta.audience_region_ids} onChange={(v) => setMeta((m) => ({ ...m, audience_region_ids: v }))} testId="paste-handbook-audience" />
              </div>
              <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-6 py-3 text-sm font-semibold text-white" data-testid="paste-handbook-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save as draft</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
